export const translations = {
  en: {
    // Common
    appName: "HealthSwipe",
    signIn: "Sign In",
    signUp: "Sign Up",
    signOut: "Log Out",
    back: "Back",
    cancel: "Cancel",
    save: "Save",
    delete: "Delete",
    edit: "Edit",
    create: "Create",
    update: "Update",
    search: "Search",
    loading: "Loading...",
    view: "View",
    viewAll: "View All",
    settings: "Settings",
    
    // Landing page
    heroTitle: "Health Assessment",
    heroTitleHighlight: "Made Simple",
    heroDescription: "Swipe through questions, get instant symptom matching, and track your health journey with our intuitive diagnostic tool.",
    getStarted: "Get Started",
    tryWithoutSignIn: "Try Without Sign In",
    noAccountNeeded: "No account needed to try",
    signInLink: "Sign in",
    toSaveHistory: "to save your history",
    forHealthcareProviders: "For Healthcare Providers",
    areYouMedicalProfessional: "Are you a Medical Professional?",
    medicalProfessionalDescription: "Manage diagnostic questions, create symptom assessments, and help patients get better preliminary guidance.",
    joinAsProfessional: "Join as Professional",
    
    // Features
    quickAssessment: "Quick Assessment",
    quickAssessmentDesc: "Answer simple questions with intuitive swipe gestures",
    symptomMatching: "Symptom Matching",
    symptomMatchingDesc: "Get matched with possible conditions based on your responses",
    trackHistory: "Track History",
    trackHistoryDesc: "Keep a record of all your health assessments",
    
    // Auth
    welcomeBack: "Welcome Back",
    createAccount: "Create Account",
    signInToContinue: "Sign in to continue your health journey",
    startYourJourney: "Start your health assessment journey",
    fullName: "Full Name",
    email: "Email",
    password: "Password",
    dontHaveAccount: "Don't have an account?",
    alreadyHaveAccount: "Already have an account?",
    patient: "Patient",
    medical: "Professional",
    admin: "Admin",
    
    // Dashboard
    welcomeBackUser: "Welcome back",
    howAreYouFeeling: "How are you feeling today?",
    startNewAssessment: "Start New Assessment",
    selectSymptomAndAnswer: "Select a symptom and answer questions",
    recentAssessments: "Recent Assessments",
    noAssessmentsYet: "No assessments yet",
    startFirstAssessment: "Start your first health assessment to get personalized insights",
    startAssessment: "Start Assessment",
    conditionsFound: "condition(s) found",
    
    // Symptom selection
    step1: "Step 1",
    selectYourSymptom: "Select Your Symptom",
    chooseMainSymptom: "Choose the main symptom you're experiencing",
    searchSymptoms: "Search symptoms...",
    noSymptomsFound: "No symptoms found",
    tryDifferentSearch: "Try a different search term or contact support",
    continueWith: "Continue with",
    
    // Swipe diagnosis
    step2: "Step 2",
    answerQuestions: "Answer Questions",
    swipeRightYes: "Swipe right for Yes, left for No",
    questionOf: "Question {current} of {total}",
    yes: "YES",
    no: "NO",
    analyzingResponses: "Analyzing your responses...",
    loadingQuestions: "Loading questions...",
    
    // Results
    assessmentResults: "Assessment Results",
    completedOn: "Completed on",
    possibleConditions: "Possible Conditions",
    match: "Match",
    recommendations: "Recommendations",
    noMatchingConditions: "No matching conditions found",
    noMatchingConditionsDesc: "Based on your responses, we couldn't identify specific conditions. Consider consulting a healthcare professional.",
    recommendedProducts: "Recommended Products",
    viewProduct: "View Product",
    medicalDisclaimer: "Medical Disclaimer",
    medicalDisclaimerText: "This assessment is for informational purposes only and should not replace professional medical advice. Please consult a healthcare provider for proper diagnosis and treatment.",
    yourResponses: "Your Responses",
    newAssessment: "New Assessment",
    viewHistory: "View History",
    signUpToSave: "Sign Up to Save",
    saveYourResults: "Save Your Results",
    signInToSave: "Sign in to save this assessment and track your health history",
    signUpFree: "Sign Up Free",
    
    // History
    assessmentHistory: "Assessment History",
    viewAllPastAssessments: "View all your past health assessments",
    searchBySymptom: "Search by symptom...",
    noHistoryYet: "No history yet",
    completedAssessmentsWillAppear: "Your completed assessments will appear here",
    topMatch: "Top match",
    noConditionsFound: "No conditions found",
    
    // Medical/Admin Dashboard
    medicalDashboard: "Medical Professional Dashboard",
    adminDashboard: "Admin Dashboard",
    welcomeDoctor: "Welcome, Dr.",
    welcomeAdmin: "Welcome,",
    manageDiagnosticQuestions: "Manage diagnostic questions and view analytics",
    manageProductsAndAnalytics: "Manage products, questions, and analytics",
    totalPatients: "Total Patients",
    totalDiagnoses: "Total Diagnoses",
    questions: "Questions",
    symptoms: "Symptoms",
    conditions: "Conditions",
    products: "Products",
    quickActions: "Quick Actions",
    manageProducts: "Manage Products",
    manageProductsDesc: "Add, edit product recommendations",
    manageQuestions: "Manage Questions",
    manageQuestionsDesc: "Add, edit diagnostic questions",
    manageSymptoms: "Manage Symptoms",
    manageSymptomsDesc: "Configure symptom categories",
    
    // Question Management
    questionsTitle: "Questions",
    manageDiagnosticQuestionsDesc: "Manage diagnostic questions",
    addQuestion: "Add Question",
    newQuestion: "New Question",
    editQuestion: "Edit Question",
    questionText: "Question Text",
    enterQuestion: "Enter question...",
    linkedSymptoms: "Linked Symptoms",
    linkedConditions: "Linked Conditions (triggers on \"Yes\")",
    weight: "Weight (1-10)",
    order: "Order",
    noQuestionsYet: "No questions yet",
    createFirstQuestion: "Create your first diagnostic question",
    filterBySymptom: "Filter by symptom",
    allSymptoms: "All Symptoms",
    
    // Symptom Management
    symptomsAndConditions: "Symptoms & Conditions",
    manageSymptomsAndConditions: "Manage symptoms and medical conditions",
    addSymptom: "Add Symptom",
    newSymptom: "New Symptom",
    editSymptom: "Edit Symptom",
    symptomName: "Name",
    category: "Category",
    description: "Description",
    noSymptomsYet: "No symptoms",
    addFirstSymptom: "Add your first symptom",
    addCondition: "Add Condition",
    newCondition: "New Condition",
    editCondition: "Edit Condition",
    conditionName: "Name",
    severity: "Severity",
    mild: "Mild",
    moderate: "Moderate",
    severe: "Severe",
    noConditionsYet: "No conditions",
    addFirstCondition: "Add your first condition",
    
    // Product Management
    productsTitle: "Products",
    manageProductRecommendations: "Manage product recommendations for conditions",
    addProduct: "Add Product",
    newProduct: "New Product",
    editProduct: "Edit Product",
    productName: "Product Name",
    productDescription: "Description",
    imageUrl: "Image URL",
    productImage: "Product Image",
    uploadFile: "Upload File",
    chooseFile: "Choose File",
    productLink: "Product Link",
    price: "Price",
    linkedConditionsProducts: "Linked Conditions (show for these diagnoses)",
    noProductsYet: "No products yet",
    addProductsToRecommend: "Add products to recommend after diagnoses",
    
    // Settings
    settingsTitle: "Settings",
    profile: "Profile",
    appearance: "Appearance",
    darkMode: "Dark Mode",
    lightThemeActive: "Light theme active",
    darkThemeActive: "Dark theme active",
    about: "About",
    aboutDescription: "A simple and intuitive health assessment tool that helps you understand your symptoms through quick, swipe-based questionnaires.",
    version: "Version 1.0.0 • This app is for informational purposes only and does not replace professional medical advice.",
    
    // Language
    language: "Language",
    selectLanguage: "Select Language",
    english: "English",
    portuguese: "Portuguese",
  },
  
  pt: {
    // Common
    appName: "HealthSwipe",
    signIn: "Entrar",
    signUp: "Registar",
    signOut: "Sair",
    back: "Voltar",
    cancel: "Cancelar",
    save: "Guardar",
    delete: "Eliminar",
    edit: "Editar",
    create: "Criar",
    update: "Atualizar",
    search: "Pesquisar",
    loading: "A carregar...",
    view: "Ver",
    viewAll: "Ver Tudo",
    settings: "Definições",
    
    // Landing page
    heroTitle: "Avaliação de Saúde",
    heroTitleHighlight: "Simplificada",
    heroDescription: "Deslize pelas perguntas, obtenha correspondência instantânea de sintomas e acompanhe a sua jornada de saúde com a nossa ferramenta de diagnóstico intuitiva.",
    getStarted: "Começar",
    tryWithoutSignIn: "Experimentar sem Conta",
    noAccountNeeded: "Não precisa de conta para experimentar",
    signInLink: "Entre",
    toSaveHistory: "para guardar o seu histórico",
    forHealthcareProviders: "Para Profissionais de Saúde",
    areYouMedicalProfessional: "É Profissional de Saúde?",
    medicalProfessionalDescription: "Gerir perguntas de diagnóstico, criar avaliações de sintomas e ajudar os pacientes a obter orientação preliminar.",
    joinAsProfessional: "Juntar-se como Profissional",
    
    // Features
    quickAssessment: "Avaliação Rápida",
    quickAssessmentDesc: "Responda a perguntas simples com gestos de deslizar intuitivos",
    symptomMatching: "Correspondência de Sintomas",
    symptomMatchingDesc: "Obtenha correspondência com possíveis condições com base nas suas respostas",
    trackHistory: "Acompanhar Histórico",
    trackHistoryDesc: "Mantenha um registo de todas as suas avaliações de saúde",
    
    // Auth
    welcomeBack: "Bem-vindo de Volta",
    createAccount: "Criar Conta",
    signInToContinue: "Entre para continuar a sua jornada de saúde",
    startYourJourney: "Comece a sua jornada de avaliação de saúde",
    fullName: "Nome Completo",
    email: "Email",
    password: "Palavra-passe",
    dontHaveAccount: "Não tem conta?",
    alreadyHaveAccount: "Já tem conta?",
    patient: "Paciente",
    medical: "Profissional",
    admin: "Admin",
    
    // Dashboard
    welcomeBackUser: "Bem-vindo de volta",
    howAreYouFeeling: "Como se sente hoje?",
    startNewAssessment: "Iniciar Nova Avaliação",
    selectSymptomAndAnswer: "Selecione um sintoma e responda às perguntas",
    recentAssessments: "Avaliações Recentes",
    noAssessmentsYet: "Sem avaliações ainda",
    startFirstAssessment: "Inicie a sua primeira avaliação de saúde para obter informações personalizadas",
    startAssessment: "Iniciar Avaliação",
    conditionsFound: "condição(ões) encontrada(s)",
    
    // Symptom selection
    step1: "Passo 1",
    selectYourSymptom: "Selecione o Seu Sintoma",
    chooseMainSymptom: "Escolha o principal sintoma que está a sentir",
    searchSymptoms: "Pesquisar sintomas...",
    noSymptomsFound: "Nenhum sintoma encontrado",
    tryDifferentSearch: "Tente um termo de pesquisa diferente ou contacte o suporte",
    continueWith: "Continuar com",
    
    // Swipe diagnosis
    step2: "Passo 2",
    answerQuestions: "Responder às Perguntas",
    swipeRightYes: "Deslize para a direita para Sim, esquerda para Não",
    questionOf: "Pergunta {current} de {total}",
    yes: "SIM",
    no: "NÃO",
    analyzingResponses: "A analisar as suas respostas...",
    loadingQuestions: "A carregar perguntas...",
    
    // Results
    assessmentResults: "Resultados da Avaliação",
    completedOn: "Concluído em",
    possibleConditions: "Possíveis Condições",
    match: "Correspondência",
    recommendations: "Recomendações",
    noMatchingConditions: "Nenhuma condição correspondente encontrada",
    noMatchingConditionsDesc: "Com base nas suas respostas, não foi possível identificar condições específicas. Considere consultar um profissional de saúde.",
    recommendedProducts: "Produtos Recomendados",
    viewProduct: "Ver Produto",
    medicalDisclaimer: "Aviso Médico",
    medicalDisclaimerText: "Esta avaliação é apenas para fins informativos e não deve substituir o aconselhamento médico profissional. Por favor, consulte um profissional de saúde para diagnóstico e tratamento adequados.",
    yourResponses: "As Suas Respostas",
    newAssessment: "Nova Avaliação",
    viewHistory: "Ver Histórico",
    signUpToSave: "Registar para Guardar",
    saveYourResults: "Guardar os Seus Resultados",
    signInToSave: "Entre para guardar esta avaliação e acompanhar o seu histórico de saúde",
    signUpFree: "Registar Grátis",
    
    // History
    assessmentHistory: "Histórico de Avaliações",
    viewAllPastAssessments: "Ver todas as suas avaliações de saúde anteriores",
    searchBySymptom: "Pesquisar por sintoma...",
    noHistoryYet: "Sem histórico ainda",
    completedAssessmentsWillAppear: "As suas avaliações concluídas aparecerão aqui",
    topMatch: "Principal correspondência",
    noConditionsFound: "Nenhuma condição encontrada",
    
    // Medical/Admin Dashboard
    medicalDashboard: "Painel do Profissional",
    adminDashboard: "Painel de Administração",
    welcomeDoctor: "Bem-vindo, Dr.",
    welcomeAdmin: "Bem-vindo,",
    manageDiagnosticQuestions: "Gerir perguntas de diagnóstico e ver estatísticas",
    manageProductsAndAnalytics: "Gerir produtos, perguntas e estatísticas",
    totalPatients: "Total de Pacientes",
    totalDiagnoses: "Total de Diagnósticos",
    questions: "Perguntas",
    symptoms: "Sintomas",
    conditions: "Condições",
    products: "Produtos",
    quickActions: "Ações Rápidas",
    manageProducts: "Gerir Produtos",
    manageProductsDesc: "Adicionar, editar recomendações de produtos",
    manageQuestions: "Gerir Perguntas",
    manageQuestionsDesc: "Adicionar, editar perguntas de diagnóstico",
    manageSymptoms: "Gerir Sintomas",
    manageSymptomsDesc: "Configurar categorias de sintomas",
    
    // Question Management
    questionsTitle: "Perguntas",
    manageDiagnosticQuestionsDesc: "Gerir perguntas de diagnóstico",
    addQuestion: "Adicionar Pergunta",
    newQuestion: "Nova Pergunta",
    editQuestion: "Editar Pergunta",
    questionText: "Texto da Pergunta",
    enterQuestion: "Introduza a pergunta...",
    linkedSymptoms: "Sintomas Associados",
    linkedConditions: "Condições Associadas (ativa com \"Sim\")",
    weight: "Peso (1-10)",
    order: "Ordem",
    noQuestionsYet: "Sem perguntas ainda",
    createFirstQuestion: "Crie a sua primeira pergunta de diagnóstico",
    filterBySymptom: "Filtrar por sintoma",
    allSymptoms: "Todos os Sintomas",
    
    // Symptom Management
    symptomsAndConditions: "Sintomas e Condições",
    manageSymptomsAndConditions: "Gerir sintomas e condições médicas",
    addSymptom: "Adicionar Sintoma",
    newSymptom: "Novo Sintoma",
    editSymptom: "Editar Sintoma",
    symptomName: "Nome",
    category: "Categoria",
    description: "Descrição",
    noSymptomsYet: "Sem sintomas",
    addFirstSymptom: "Adicione o seu primeiro sintoma",
    addCondition: "Adicionar Condição",
    newCondition: "Nova Condição",
    editCondition: "Editar Condição",
    conditionName: "Nome",
    severity: "Gravidade",
    mild: "Ligeira",
    moderate: "Moderada",
    severe: "Grave",
    noConditionsYet: "Sem condições",
    addFirstCondition: "Adicione a sua primeira condição",
    
    // Product Management
    productsTitle: "Produtos",
    manageProductRecommendations: "Gerir recomendações de produtos para condições",
    addProduct: "Adicionar Produto",
    newProduct: "Novo Produto",
    editProduct: "Editar Produto",
    productName: "Nome do Produto",
    productDescription: "Descrição",
    imageUrl: "URL da Imagem",
    productImage: "Imagem do Produto",
    uploadFile: "Carregar Ficheiro",
    chooseFile: "Escolher Ficheiro",
    productLink: "Link do Produto",
    price: "Preço",
    linkedConditionsProducts: "Condições Associadas (mostrar para estes diagnósticos)",
    noProductsYet: "Sem produtos ainda",
    addProductsToRecommend: "Adicione produtos para recomendar após os diagnósticos",
    
    // Settings
    settingsTitle: "Definições",
    profile: "Perfil",
    appearance: "Aparência",
    darkMode: "Modo Escuro",
    lightThemeActive: "Tema claro ativo",
    darkThemeActive: "Tema escuro ativo",
    about: "Sobre",
    aboutDescription: "Uma ferramenta de avaliação de saúde simples e intuitiva que ajuda a compreender os seus sintomas através de questionários rápidos baseados em deslizar.",
    version: "Versão 1.0.0 • Esta aplicação é apenas para fins informativos e não substitui o aconselhamento médico profissional.",
    
    // Language
    language: "Idioma",
    selectLanguage: "Selecionar Idioma",
    english: "Inglês",
    portuguese: "Português",
  }
};

export const getTranslation = (lang, key) => {
  return translations[lang]?.[key] || translations.en[key] || key;
};
